import { Injectable } from '@angular/core';

const LS_KEY = 'fitmed-device-id';
const COOKIE_KEY = 'fitmed_device_id';

@Injectable({ providedIn: 'root' })
export class DeviceIdService {
  private cached?: string;

  /** Public: returns a stable, per-browser device id (UUID). */
  get(): string {
    if (this.cached) return this.cached;

    // 1) localStorage
    try {
      const fromLs = localStorage.getItem(LS_KEY);
      if (fromLs) return (this.cached = fromLs);
    } catch { /* private mode or blocked */ }

    // 2) cookie fallback
    const fromCookie = this.readCookie(COOKIE_KEY);
    if (fromCookie) {
      this.persistAll(fromCookie);
      return (this.cached = fromCookie);
    }

    // 3) generate new
    const id = this.generateUuid();
    this.persistAll(id);
    return (this.cached = id);
  }

  // ───────────────────────────────────────── internals
  private persistAll(id: string) {
    try { localStorage.setItem(LS_KEY, id); } catch {}
    this.writeCookie(COOKIE_KEY, id, 3650); // ~10 years
  }

  private generateUuid(): string {
    // Prefer browser crypto.randomUUID
    if (typeof window !== 'undefined' && window.crypto && 'randomUUID' in window.crypto) {
      return window.crypto.randomUUID();
    }

    // RFC4122 v4 fallback
    const b = new Uint8Array(16);
    if (typeof window !== 'undefined' && window.crypto?.getRandomValues) {
      window.crypto.getRandomValues(b);
    } else {
      // super-old fallback (not crypto secure)
      for (let i = 0; i < 16; i++) b[i] = Math.floor(Math.random() * 256);
    }
    b[6] = (b[6] & 0x0f) | 0x40; // version 4
    b[8] = (b[8] & 0x3f) | 0x80; // variant
    const toHex = (n: number) => n.toString(16).padStart(2, '0');
    const hex = Array.from(b, toHex).join('');
    return `${hex.slice(0,8)}-${hex.slice(8,12)}-${hex.slice(12,16)}-${hex.slice(16,20)}-${hex.slice(20)}`;
  }

  private writeCookie(name: string, value: string, days: number) {
    const d = new Date();
    d.setTime(d.getTime() + days * 24 * 60 * 60 * 1000);
    document.cookie = `${encodeURIComponent(name)}=${encodeURIComponent(value)}; expires=${d.toUTCString()}; path=/; SameSite=Lax`;
  }

  private readCookie(name: string): string | null {
    const key = encodeURIComponent(name) + '=';
    const parts = document.cookie.split(';');
    for (let c of parts) {
      c = c.trim();
      if (c.startsWith(key)) return decodeURIComponent(c.substring(key.length));
    }
    return null;
  }
}
